'use client'

import { useState, useEffect, useMemo } from 'react'
import { cn } from '@/lib/tailwind'
import {
  SparkleIcon,
  BrainIcon,
  MagnifyingGlassIcon,
  LightbulbIcon,
  PencilSimpleIcon,
} from '@phosphor-icons/react'
import type { Icon as PhosphorIcon } from '@phosphor-icons/react'

/** Message configuration for the thinking indicator */
export interface ThinkingMessage {
  text: string
  Icon: PhosphorIcon
}

// Warm, refined thinking messages aligned with Konstantout's personality
// Voice: Calm and confident, like a trusted companion
const THINKING_MESSAGES: ThinkingMessage[] = [
  { text: 'Reading between the lines...', Icon: MagnifyingGlassIcon },
  { text: "Picking up what you're putting down...", Icon: LightbulbIcon },
  { text: 'Connecting the threads...', Icon: BrainIcon },
  { text: 'Getting the gist...', Icon: SparkleIcon },
  { text: 'Making sense of things...', Icon: BrainIcon },
  { text: 'Finding the shape of your thought...', Icon: PencilSimpleIcon },
  { text: 'Tuning in...', Icon: MagnifyingGlassIcon },
  { text: 'On it...', Icon: SparkleIcon },
  { text: 'Following your train of thought...', Icon: BrainIcon },
  { text: 'Catching the vibe...', Icon: LightbulbIcon },
]

// Fallback message if somehow no messages are provided
const FALLBACK_MESSAGE: ThinkingMessage = {
  text: 'Thinking...',
  Icon: SparkleIcon,
}

interface ThinkingIndicatorProps {
  /** Whether currently thinking/loading */
  isThinking: boolean
  /** Optional custom messages to cycle through (must have at least 1 item) */
  messages?: ThinkingMessage[]
  /** How fast to rotate messages (ms, default 2000, min 500) */
  rotationMs?: number
  /** Additional className */
  className?: string
}

/**
 * A playful thinking indicator that cycles through different messages.
 * Inspired by Claude Code's loading states.
 */
export function ThinkingIndicator({
  isThinking,
  messages: messagesProp,
  rotationMs: rotationMsProp = 2000,
  className,
}: ThinkingIndicatorProps) {
  // Ensure we always have valid messages array
  const messages =
    messagesProp && messagesProp.length > 0 ? messagesProp : THINKING_MESSAGES
  const messageCount = messages.length

  // Clamp rotation time to reasonable bounds (500ms - 10s)
  const rotationMs = Math.max(500, Math.min(10000, rotationMsProp))

  const [messageIndex, setMessageIndex] = useState(0)

  // Pick a random starting message when thinking starts
  useEffect(() => {
    if (isThinking && messageCount > 0) {
      const timeoutId = setTimeout(() => {
        setMessageIndex(Math.floor(Math.random() * messageCount))
      }, 100)
      return () => clearTimeout(timeoutId)
    }
  }, [isThinking, messageCount])

  // Rotate through messages while thinking
  useEffect(() => {
    if (!isThinking || messageCount <= 1) return

    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % messageCount)
    }, rotationMs)

    return () => clearInterval(interval)
  }, [isThinking, messageCount, rotationMs])

  // Safely get current message with bounds checking
  const currentMessage = useMemo(() => {
    if (messageCount === 0) return FALLBACK_MESSAGE
    const safeIndex = Math.min(Math.max(0, messageIndex), messageCount - 1)
    return messages[safeIndex] ?? FALLBACK_MESSAGE
  }, [messages, messageIndex, messageCount])

  if (!isThinking) return null

  const { text, Icon } = currentMessage

  return (
    <div
      className={cn(
        'text-muted-foreground inline-flex items-center gap-2 text-sm',
        className,
      )}
      role="status"
      aria-live="polite"
    >
      <span className="relative flex h-4 w-4 items-center justify-center">
        {/* Pulsing background */}
        <span className="bg-primary/20 absolute h-full w-full animate-ping rounded-full" />
        {/* Icon with subtle bounce */}
        <Icon
          className="text-primary relative h-4 w-4 animate-pulse"
          weight="duotone"
        />
      </span>
      <span className="animate-pulse">{text}</span>
    </div>
  )
}

/**
 * Inline version for use in inputs/small spaces
 */
export function ThinkingDots({ className }: { className?: string }) {
  return (
    <span
      className={cn('inline-flex items-center gap-0.5', className)}
      role="status"
      aria-label="Analyzing..."
    >
      <span className="bg-primary animate-thinking-dot h-1 w-1 rounded-full [animation-delay:-0.32s]" />
      <span className="bg-primary animate-thinking-dot h-1 w-1 rounded-full [animation-delay:-0.16s]" />
      <span className="bg-primary animate-thinking-dot h-1 w-1 rounded-full" />
    </span>
  )
}
